import { CurrentUserService } from '@/app/common/current-user/current-user.service';
import { FilterApplicationStatusDto } from '@/app/common/general-types/brief-application.dto';
import { ApplicationStatusEnum, StatusHistory } from '@/app/common/general-types/briefs.dto';
import { BriefApplicationService } from '@/app/services/brief-application/brief-application.service';
import { FormatService } from '@/app/services/format/format.service';
import { ImageService } from '@/app/services/image/image.service';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  inject,
  OnDestroy,
  signal,
  viewChild,
  CUSTOM_ELEMENTS_SCHEMA
} from '@angular/core';
import { InfiniteScrollDirective } from 'ngx-infinite-scroll';
import { Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { ApplicationStatusBadgeComponent } from '@/app/atoms/application-status-badge/application-status-badge.component';
import { Router } from '@angular/router';


const SELECTABLE_ACTIVITY_STATUSES: ApplicationStatusEnum[] = [
  ApplicationStatusEnum.Invited,
  ApplicationStatusEnum.Applied,
  ApplicationStatusEnum.Hired,
  ApplicationStatusEnum.ReadyToPost,
  // ApplicationStatusEnum.Posted,
  // ApplicationStatusEnum.Completed,
  // ApplicationStatusEnum.Paid,
  ApplicationStatusEnum.Started,
  // ApplicationStatusEnum.BonusPaid,
];


@Component({
  selector: 'dbv2-recent-activity',
  imports: [ApplicationStatusBadgeComponent, InfiniteScrollDirective],
  templateUrl: './recent-activity.html',
  styleUrl: './recent-activity.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class RecentActivity implements OnDestroy {

  activityPageItemCount = 14;
  activityPageNo = 0;
  scrollDistance = 2;
  scrollThrottle = 100;

  readonly selectableActivityStatuses = SELECTABLE_ACTIVITY_STATUSES;

  /** Statuses included in the feed (sent as inStatus to the API). */
  visibleStatuses = signal<ApplicationStatusEnum[]>([
    ApplicationStatusEnum.Invited,
    ApplicationStatusEnum.Applied,
    ApplicationStatusEnum.Hired,
    ApplicationStatusEnum.ReadyToPost,
    ApplicationStatusEnum.Started,
  ]);
  filterMenuOpen = signal(false);

  statusHistory = signal<any[] | undefined>(undefined);
  isLoadingMore = signal(false);
  hasMore = signal(true);

  /** Services */
  private router = inject(Router);
  private briefAppService = inject(BriefApplicationService);
  private currentUserService = inject(CurrentUserService);
  public imageService = inject(ImageService);
  public formatService = inject(FormatService);
  /** Services - END */

  private currentAccountId: string | undefined;
  private loadGeneration = 0;
  private readonly subscriptionList = new Subscription();
  private readonly filterMenuRef = viewChild<ElementRef<HTMLElement>>('filterMenu');

  /** Placeholder rows rendered while channels load. */
  protected readonly skeletonRows = [0, 1, 2, 3];

  constructor() {
    this.subscriptionList.add(
      this.currentUserService.focussedBrandSubject.pipe(
        debounceTime(350),
        distinctUntilChanged(),
      ).subscribe((brand: { id: string }) => {
        console.log('👽👽👽👽 Brand: ', brand);
        this.resetAndLoadActivity(brand.id);
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptionList.unsubscribe();
  }


  getStatusLabel(status: ApplicationStatusEnum): string {
    switch (status) {
      case ApplicationStatusEnum.Invited:
        return 'Invited';
      case ApplicationStatusEnum.Applied:
        return 'Applied';
      case ApplicationStatusEnum.Hired:
        return 'Hired';
      case ApplicationStatusEnum.ReadyToPost:
        return 'Post';
      case ApplicationStatusEnum.Posted:
        return 'Posted';
      case ApplicationStatusEnum.Completed:
        return 'Completed';
      case ApplicationStatusEnum.Paid:
        return 'Paid';
      case ApplicationStatusEnum.Started:
        return 'Started';
      case ApplicationStatusEnum.InviteDeclined:
        return 'Invite Declined';
      case ApplicationStatusEnum.ApplicationDeclined:
        return 'Declined';
      case ApplicationStatusEnum.InviteRemoved:
        return 'Invite Removed';
      case ApplicationStatusEnum.BonusPaid:
        return 'Bonus Paid';
      case ApplicationStatusEnum.HiredRemoved:
        return 'Un-hired';
      default:
        return 'Status';
    }
  }

  isStatusVisible(status: ApplicationStatusEnum): boolean {
    return this.visibleStatuses().includes(status);
  }

  isFilterActive(): boolean {
    const defaults = [ApplicationStatusEnum.Invited, ApplicationStatusEnum.Applied];
    const current = this.visibleStatuses();
    return (
      current.length !== defaults.length ||
      !defaults.every((s) => current.includes(s))
    );
  }

  filterStatus(status: ApplicationStatusEnum) {
    console.log('👽👽👽👽 Filter Status: ', status);
    if (this.visibleStatuses().includes(status)) {
      this.visibleStatuses.update((prev) => prev.filter((s) => s !== status));
    } else {
      this.visibleStatuses.update((prev) => [...prev, status]);
    }

    if (this.currentAccountId) {
      this.resetAndLoadActivity(this.currentAccountId);
    }
  }


  private reloadActivityWithCurrentFilter(): void {
    if (!this.currentAccountId) {
      return;
    }
    this.resetAndLoadActivity(this.currentAccountId);
  }

  resetAndLoadActivity(accountId: string): void {
    this.currentAccountId = accountId;
    this.activityPageNo = 0;
    this.hasMore.set(true);
    this.statusHistory.set(undefined);
    this.getRecentActivity(accountId, true);
  }

  onScrollDown(): void {
    if (this.isLoadingMore() || !this.hasMore() || !this.currentAccountId) {
      return;
    }
    this.getRecentActivity(this.currentAccountId);
  }

  getRecentActivity(accountId: string, isReset = false): void {
    if (this.isLoadingMore()) {
      return;
    }

    const filter: FilterApplicationStatusDto = {
      skip: this.activityPageItemCount * this.activityPageNo,
      take: this.activityPageItemCount,
      notInStatus: [],
      inStatus: this.visibleStatuses(),
    };

    const generation = ++this.loadGeneration;
    this.isLoadingMore.set(true);

    this.subscriptionList.add(
      this.briefAppService.getRecentActivityBusinessFilter(accountId, filter).subscribe({
        next: (response: StatusHistory[]) => {
          if (generation !== this.loadGeneration) {
            return;
          }

          if (isReset || !this.statusHistory()) {
            this.statusHistory.set(response);
          } else {
            this.statusHistory.update(prev => [...(prev ?? []), ...response]);
          }

          this.hasMore.set(response.length >= this.activityPageItemCount);
          this.activityPageNo++;
          this.isLoadingMore.set(false);
        },
        error: () => {
          if (generation === this.loadGeneration) {
            this.isLoadingMore.set(false);
          }
        },
      }),
    );
  }

  onApplicationClick(application: any): void {
    console.log('👽👽👽👽 Application Click: ', application);
    switch (application.applicationStatus) {
      case ApplicationStatusEnum.Invited:
        this.router.navigate(['/manage-brief', application.briefId, 2, 1]);
        break;
      case ApplicationStatusEnum.Applied:
        this.router.navigate(['/manage-brief', application.briefId, 2, 2]);
        break;
      case ApplicationStatusEnum.Hired:
        this.router.navigate(['/view-submission/', application.briefId, application.creatorUserId]);
        break;
      case ApplicationStatusEnum.ReadyToPost:
        this.router.navigate(['/view-submission/', application.briefId, application.creatorUserId]);
        break;
      case ApplicationStatusEnum.Started:
        this.router.navigate(['/view-submission/', application.briefId, application.creatorUserId]);
        break;
      case ApplicationStatusEnum.InviteDeclined:
        this.router.navigate(['/manage-brief', application.briefId, 2, 0]);
        break;
      case ApplicationStatusEnum.ApplicationDeclined:
        this.router.navigate(['/manage-brief', application.briefId, 2, 0]);
        break;
      case ApplicationStatusEnum.InviteRemoved:
        this.router.navigate(['/manage-brief', application.briefId, 2, 0]);
        break;
      case ApplicationStatusEnum.BonusPaid:
        this.router.navigate(['/view-submission/', application.briefId, application.creatorUserId]);
        break;
      case ApplicationStatusEnum.HiredRemoved:
        this.router.navigate(['/manage-brief', application.briefId, 2, 0]);
        break;
      default:
        break;
    }
  }
}
